import { StandardProps } from "../componentMap";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { InfoOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InfoOutlined";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import { useState } from "react";
import { GreenTickSvg } from "@/components/PricePromise/GreenTickSvg.tsx";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import {
  pricingGroups,
  getPromoMessaging,
} from "@/utils/product/getPromoMessaging";
import { getPromotionalMessagesToShow } from "@/utils/product/getPromotionalMessagesToShow";
import styles from "./PricePromise.module.scss";
import {
  EXCLUDED_CATEGORIES,
  EXCLUDED_BRANDS,
  EXCLUDED_PRODUCT_ID,
  EXCLUDED_PRODUCT_TYPE,
} from "@/utils/pricePromise/PricePromiseExclusions.ts";

export const PricePromise = ({
  product,
  flags,
  isInGroup = false,
}: Pick<StandardProps, "product" | "flags"> & { isInGroup?: boolean }) => {
  const moveReviewsSkipLink = flags?.moveReviewsSkipLink.value === true;

  const [showPricePromiseTooltip, setShowPricePromiseTooltip] = useState(false);
  const { variant, isSelected } = useVariant(product);

  const hasExclusiveAdditional = product?.messaging?.some(
    (msg) =>
      msg.type === "additional" &&
      typeof msg.title === "string" &&
      msg.title.toLowerCase().includes("exclusive"),
  );

  const isJohnLewisProduct =
    typeof product?.brand?.name === "string" &&
    ["john lewis", "anyday", "and/or", "kin"].some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const variantGroup = getColourGroupForVariant(
    product.variantGroups,
    variant?.id,
  );

  const variantGroupHasPromos = variantGroup?.messaging?.some(
    (msg) =>
      "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
  );

  const productGroupHasPromos = product?.messaging?.some(
    (msg) =>
      "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
  );

  const groupHasPromos = variantGroupHasPromos || productGroupHasPromos;

  const categoryIsExcluded = product?.browsePageHierarchy?.some((page) =>
    EXCLUDED_CATEGORIES.includes(page.categoryId),
  );

  const brandIsExcluded =
    typeof product?.brand?.name === "string" &&
    EXCLUDED_BRANDS.some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const hasPromoMessaging = !!getPromoMessaging(
    getPromotionalMessagesToShow(product, variant, isSelected, variantGroup),
  ).pageMessages.length;

  const hasPriceOffer = !!variant?.price?.offerType;

  if (
    hasExclusiveAdditional ||
    hasPromoMessaging ||
    hasPriceOffer ||
    categoryIsExcluded ||
    brandIsExcluded ||
    product.aliases.pimProductId === EXCLUDED_PRODUCT_ID ||
    product.__typename === EXCLUDED_PRODUCT_TYPE ||
    groupHasPromos
  )
    return null;

  const tooltipContent = isJohnLewisProduct ? (
    <p
      className={styles.pricePromiseTooltipMessage}
      data-testid="price-promise-tooltip-message"
    >
      If the price of this item drops within 7 days of purchase, you can claim
      back the difference.{" "}
      <a
        target="_blank"
        href="https://www.johnlewis.com/customer-services/prices-and-payment/price-promise?source=pricepromisetooltip"
        rel="noreferrer"
        data-testid="price-promise-tooltip-link"
      >
        T&amp;Cs apply
      </a>
    </p>
  ) : (
    <p
      className={styles.pricePromiseTooltipMessage}
      data-testid="price-promise-tooltip-message"
    >
      We match the price of this item and thousands more with 25 top retailers.
      If the price drops within 7 days of purchase, you can claim back the
      difference.{" "}
      <a
        target="_blank"
        href="https://www.johnlewis.com/customer-services/prices-and-payment/price-promise?source=pricepromisetooltip"
        rel="noreferrer"
        data-testid="price-promise-tooltip-link"
      >
        T&amp;Cs apply
      </a>
    </p>
  );

  const containerClassname = isInGroup
    ? styles.pricePromiseGroupContainer
    : styles.pricePromiseContainer;

  return (
    <div className={containerClassname} data-testid="price-promise-container">
      <div className={styles.pricePromiseInfoContainer}>
        <GreenTickSvg />
        <p className={styles.pricePromiseMessage}>Price promise</p>
        {moveReviewsSkipLink ? (
          <Tooltip
            anchor="right"
            buttonLabel=""
            ariaLabel="Find out more about our price promise"
            id="tooltip-standard"
            verticalAnchor="top"
            icon={true}
            data-testid="price-promise-info-button"
            onOpen={() => {
              dispatchMonetateQEvent("WPDP | Price Promise Info Displayed");
            }}
            popoverClassname={styles.pricePromiseGroupTooltip}
          >
            {tooltipContent}
          </Tooltip>
        ) : (
          <IconButton
            icon={InfoOutlined}
            aria-label="Info Button"
            onClick={() => {
              !showPricePromiseTooltip
                ? setShowPricePromiseTooltip(true)
                : setShowPricePromiseTooltip(false);

              if (!showPricePromiseTooltip) {
                dispatchMonetateQEvent("WPDP | Price Promise Info Displayed");
              }
            }}
            className={styles.pricePromiseButton}
            data-testid="price-promise-info-button"
          />
        )}
      </div>
      {showPricePromiseTooltip && !moveReviewsSkipLink && (
        <div
          className={styles.pricePromiseTooltipContainer}
          data-testid="price-promise-tooltip-container"
        >
          {tooltipContent}
          <IconButton
            icon={CloseOutlined}
            aria-label="Close Button"
            onClick={() => setShowPricePromiseTooltip(false)}
            className={styles.pricePromiseButton}
            data-testid="price-promise-close-button"
          />
        </div>
      )}
    </div>
  );
};
